// Forced first-login password change. Rendered by AuthGuard INSTEAD of the
// app's children whenever agent.mustChangePassword is true — blocks the rest
// of the dashboard (Layout never mounts) until the user sets their own
// password. This is the tenant-side counterpart to the super-admin's
// "Add admin user" action: a temp password the super-admin sets and sends is
// only ever valid to get here, never to reach the real dashboard.

import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { changePassword } from '../api/auth';
import { ApiError } from '../api/client';
import { ThemeToggle } from './ThemeToggle';
import { ProjectLockup } from './ProjectLockup';

export function ForcedPasswordChange() {
  const { refreshAgent } = useAuth();
  const { t, i18n } = useTranslation();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  function toggleLanguage() {
    void i18n.changeLanguage(i18n.language === 'ar' ? 'en' : 'ar');
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError(t('force_password_change.error_mismatch'));
      return;
    }
    if (newPassword.length < 8) {
      setError(t('force_password_change.error_too_short'));
      return;
    }

    setLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      // Same session cookie stays valid — just re-fetch /me so
      // mustChangePassword flips to false and AuthGuard renders the real app.
      await refreshAgent();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(t('force_password_change.error_credentials'));
      } else {
        setError(t('force_password_change.error_generic'));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="hero-atmosphere min-h-screen flex items-center justify-center p-4">
      <div className="fixed top-4 end-4 flex items-center gap-2">
        <ThemeToggle showLabel={false} />
        <button
          onClick={toggleLanguage}
          data-testid="lang-toggle"
          className="h-8 px-3 text-xs font-medium rounded-brand-md
            border border-default text-secondary hover:text-primary
            hover:bg-surface-hover transition-colors duration-base ease-standard"
        >
          {t('lang_toggle')}
        </button>
      </div>

      <div
        className="w-full max-w-sm rounded-[1.5rem] border border-default/80 bg-surface p-8"
        style={{ border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-lg), var(--shadow-inset)' }}
      >
        <div className="text-center mb-6">
          <div className="flex justify-center mb-1">
            <ProjectLockup variant="hero" />
          </div>
          <p className="text-sm font-medium text-primary mt-4">{t('force_password_change.title')}</p>
          <p className="text-xs text-muted mt-1.5">{t('force_password_change.subtitle')}</p>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
          <div>
            <label htmlFor="force-pwd-current" className="block text-sm font-medium text-secondary mb-1">
              {t('force_password_change.current_password_label')}
            </label>
            <input
              id="force-pwd-current"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="h-11 w-full rounded-brand-md px-4 text-sm
                bg-canvas text-primary placeholder:text-muted
                border border-default
                focus:outline-none focus:border-strong
                transition-colors duration-base ease-standard"
            />
          </div>

          <div>
            <label htmlFor="force-pwd-new" className="block text-sm font-medium text-secondary mb-1">
              {t('force_password_change.new_password_label')}
            </label>
            <input
              id="force-pwd-new"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="h-11 w-full rounded-brand-md px-4 text-sm
                bg-canvas text-primary placeholder:text-muted
                border border-default
                focus:outline-none focus:border-strong
                transition-colors duration-base ease-standard"
            />
          </div>

          <div>
            <label htmlFor="force-pwd-confirm" className="block text-sm font-medium text-secondary mb-1">
              {t('force_password_change.confirm_password_label')}
            </label>
            <input
              id="force-pwd-confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="h-11 w-full rounded-brand-md px-4 text-sm
                bg-canvas text-primary placeholder:text-muted
                border border-default
                focus:outline-none focus:border-strong
                transition-colors duration-base ease-standard"
            />
          </div>

          {error && (
            <div
              className="bg-tint-danger rounded-brand-sm px-4 py-2.5 text-sm text-center"
              style={{ border: '1px solid var(--tint-danger)', color: 'var(--status-danger)' }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="touch-target w-full h-11 rounded-brand-md font-semibold text-sm
              bg-action-primary text-action-primary
              disabled:opacity-40
              transition-all duration-base ease-enter
              hover:-translate-y-px hover:shadow-md
              active:translate-y-0 active:shadow-sm"
          >
            {loading ? t('force_password_change.submitting') : t('force_password_change.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}
